import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const ManufacturingOverviewDashboard: React.FC = () => {
  const kpis = [{
    title: 'Total Output',
    value: '2.45M Units',
    change: '+8.2%',
    isPositive: true
  }, {
    title: 'Cost per Unit',
    value: '₹142',
    change: '-3.5%',
    isPositive: true
  }, {
    title: 'Scrap Rate',
    value: '2.1%',
    change: '-0.4%',
    isPositive: true
  }, {
    title: 'Overall OEE',
    value: '78.5%',
    change: '+2.8%',
    isPositive: true
  }, {
    title: 'Plant Efficiency',
    value: '92/100',
    change: '+3 pts',
    isPositive: true
  }];
  const plantPerformanceData = [{
    plant: 'Manesar',
    output: 685000,
    oee: 76,
    efficiency: 92,
    costPerUnit: 142
  }, {
    plant: 'Chennai',
    output: 725000,
    oee: 82,
    efficiency: 94,
    costPerUnit: 138
  }, {
    plant: 'Pune',
    output: 545000,
    oee: 75,
    efficiency: 90,
    costPerUnit: 145
  }, {
    plant: 'Bangalore',
    output: 495000,
    oee: 79,
    efficiency: 93,
    costPerUnit: 140
  }];
  const monthlyTrendData = [{
    month: 'Jan',
    output: 195000,
    oee: 75.2,
    scrap: 2.5
  }, {
    month: 'Feb',
    output: 198000,
    oee: 76.1,
    scrap: 2.4
  }, {
    month: 'Mar',
    output: 202000,
    oee: 76.8,
    scrap: 2.3
  }, {
    month: 'Apr',
    output: 205000,
    oee: 77.2,
    scrap: 2.2
  }, {
    month: 'May',
    output: 208000,
    oee: 77.8,
    scrap: 2.1
  }, {
    month: 'Jun',
    output: 212000,
    oee: 78.5,
    scrap: 2.1
  }, {
    month: 'Jul',
    output: 215000,
    oee: 78.8,
    scrap: 2.0
  }, {
    month: 'Aug',
    output: 218000,
    oee: 79.2,
    scrap: 1.9
  }, {
    month: 'Sep',
    output: 210000,
    oee: 78.5,
    scrap: 2.0
  }, {
    month: 'Oct',
    output: 222000,
    oee: 79.5,
    scrap: 1.8
  }, {
    month: 'Nov',
    output: 225000,
    oee: 80.2,
    scrap: 1.7
  }, {
    month: 'Dec',
    output: 228000,
    oee: 80.8,
    scrap: 1.6
  }];
  const productLineMixData = [{
    line: 'Lighting',
    output: 685000,
    percentage: 28
  }, {
    line: 'Electrical',
    output: 735000,
    percentage: 30
  }, {
    line: 'Polymer',
    output: 588000,
    percentage: 24
  }, {
    line: 'Safety',
    output: 442000,
    percentage: 18
  }];
  // OEE Breakdown Trend Data (Availability, Performance, Quality)
  const oeeBreakdownData = [{
    month: 'Jan',
    availability: 85.2,
    performance: 90.5,
    quality: 97.5,
    oee: 75.2
  }, {
    month: 'Feb',
    availability: 85.8,
    performance: 91.0,
    quality: 97.6,
    oee: 76.1
  }, {
    month: 'Mar',
    availability: 86.2,
    performance: 91.2,
    quality: 97.7,
    oee: 76.8
  }, {
    month: 'Apr',
    availability: 86.5,
    performance: 91.5,
    quality: 97.8,
    oee: 77.2
  }, {
    month: 'May',
    availability: 87.0,
    performance: 91.8,
    quality: 97.5,
    oee: 77.8
  }, {
    month: 'Jun',
    availability: 87.5,
    performance: 92.0,
    quality: 97.6,
    oee: 78.5
  }, {
    month: 'Jul',
    availability: 87.8,
    performance: 92.2,
    quality: 97.4,
    oee: 78.8
  }, {
    month: 'Aug',
    availability: 88.2,
    performance: 92.5,
    quality: 97.1,
    oee: 79.2
  }, {
    month: 'Sep',
    availability: 87.8,
    performance: 92.0,
    quality: 97.2,
    oee: 78.5
  }, {
    month: 'Oct',
    availability: 88.5,
    performance: 92.8,
    quality: 96.9,
    oee: 79.5
  }, {
    month: 'Nov',
    availability: 89.0,
    performance: 93.0,
    quality: 96.8,
    oee: 80.2
  }, {
    month: 'Dec',
    availability: 89.5,
    performance: 93.2,
    quality: 96.9,
    oee: 80.8
  }];
  // Plant-wise Summary Table Data
  const plantSummaryData = [{
    plant: 'Manesar',
    output: 685000,
    oee: 76.5,
    availability: 87.2,
    performance: 91.5,
    quality: 96.0,
    scrapRate: 2.1,
    costPerUnit: 142,
    efficiency: 92,
    downtime: 12.8,
    utilizationRate: 85.5
  }, {
    plant: 'Chennai',
    output: 725000,
    oee: 82.0,
    availability: 90.5,
    performance: 93.5,
    quality: 97.0,
    scrapRate: 1.8,
    costPerUnit: 138,
    efficiency: 94,
    downtime: 9.5,
    utilizationRate: 88.2
  }, {
    plant: 'Pune',
    output: 545000,
    oee: 75.0,
    availability: 86.0,
    performance: 90.5,
    quality: 96.5,
    scrapRate: 2.4,
    costPerUnit: 145,
    efficiency: 90,
    downtime: 14.0,
    utilizationRate: 82.8
  }, {
    plant: 'Bangalore',
    output: 495000,
    oee: 79.0,
    availability: 88.5,
    performance: 92.0,
    quality: 97.0,
    scrapRate: 1.9,
    costPerUnit: 140,
    efficiency: 93,
    downtime: 11.5,
    utilizationRate: 86.5
  }];
  return <div data-id="element-5605">
      <DescriptionPanel title="Manufacturing Operations Overview" description="Comprehensive view of manufacturing performance across all plants. Monitor production output, quality metrics, OEE, and cost efficiency to drive operational excellence and continuous improvement." insights={['Chennai plant leading efficiency at 94/100 with lowest cost per unit (₹138)', 'Overall OEE improved to 78.5% - 2.8% increase from last quarter', 'Scrap rate reduced to 2.1% from 2.5% - quality initiatives showing results', 'Production output up 8.2% YoY driven by capacity expansion and efficiency gains']} data-id="element-5606" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-5607">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5608">
          Manufacturing Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-5609">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-5610">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5611">
                {kpi.title}
              </div>
              <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-5612">
                {kpi.value}
              </div>
              <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-5613">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-5614" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-5615" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Plant Performance Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5616">
        <Card className="p-6" data-id="element-5617">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5618">
            Plant Performance Comparison
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-5619">
            <ComposedChart data={plantPerformanceData} layout="vertical" data-id="element-5620">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5621" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-5622" />
              <YAxis dataKey="plant" type="category" width={80} tick={{
              fontSize: 11
            }} data-id="element-5623" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-5624" />
              <Legend data-id="element-5625" />
              <Bar dataKey="oee" fill="#3b82f6" name="OEE %" data-id="element-5626" />
              <Bar dataKey="efficiency" fill="#10b981" name="Efficiency Score" data-id="element-5627" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6" data-id="element-5628">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5629">
            Output by Plant (Units)
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-5630">
            <BarChart data={plantPerformanceData} data-id="element-5631">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5632" />
              <XAxis dataKey="plant" tick={{
              fontSize: 11
            }} data-id="element-5633" />
              <YAxis tick={{
              fontSize: 11
            }} data-id="element-5634" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => value.toLocaleString()} data-id="element-5635" />
              <Bar dataKey="output" fill="#f59e0b" name="Total Output" data-id="element-5636" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* Monthly Trends and OEE Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5637">
        {/* Monthly Production Trends */}
        <Card className="p-6" data-id="element-5638">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5639">
            Monthly Production Trends
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-5640">
            <ComposedChart data={monthlyTrendData} data-id="element-5641">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5642" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-5643" />
              <YAxis yAxisId="left" tick={{
              fontSize: 12
            }} data-id="element-5644" />
              <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} data-id="element-5645" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-5646" />
              <Legend data-id="element-5647" />
              <Bar yAxisId="left" dataKey="output" fill="#3b82f6" name="Output (Units)" data-id="element-5648" />
              <Line yAxisId="right" type="monotone" dataKey="oee" stroke="#10b981" strokeWidth={2} name="OEE %" data-id="element-5649" />
              <Line yAxisId="right" type="monotone" dataKey="scrap" stroke="#ef4444" strokeWidth={2} name="Scrap %" data-id="element-5650" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        {/* OEE Breakdown Trend */}
        <Card className="p-6" data-id="element-5651">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5652">
            OEE Breakdown Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5653">
            Availability × Performance × Quality = OEE
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5654">
            <LineChart data={oeeBreakdownData} data-id="element-5655">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5656" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-5657" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[70, 100]} data-id="element-5658" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-5659" />
              <Legend data-id="element-5660" />
              <Line type="monotone" dataKey="availability" stroke="#3b82f6" strokeWidth={2} name="Availability %" dot={{
              r: 4
            }} data-id="element-5661" />
              <Line type="monotone" dataKey="performance" stroke="#10b981" strokeWidth={2} name="Performance %" dot={{
              r: 4
            }} data-id="element-5662" />
              <Line type="monotone" dataKey="quality" stroke="#f59e0b" strokeWidth={2} name="Quality %" dot={{
              r: 4
            }} data-id="element-5663" />
              <Line type="monotone" dataKey="oee" stroke="#8b5cf6" strokeWidth={3} name="Overall OEE %" dot={{
              r: 5
            }} data-id="element-5664" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5665">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-5666">
              <div className="text-center" data-id="element-5667">
                <div className="text-gray-600" data-id="element-5668">Availability</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-5669">89.5%</div>
              </div>
              <div className="text-center" data-id="element-5670">
                <div className="text-gray-600" data-id="element-5671">Performance</div>
                <div className="text-xl font-bold text-green-600" data-id="element-5672">93.2%</div>
              </div>
              <div className="text-center" data-id="element-5673">
                <div className="text-gray-600" data-id="element-5674">Quality</div>
                <div className="text-xl font-bold text-yellow-600" data-id="element-5675">96.9%</div>
              </div>
              <div className="text-center" data-id="element-5676">
                <div className="text-gray-600" data-id="element-5677">OEE</div>
                <div className="text-xl font-bold text-purple-600" data-id="element-5678">80.8%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Product Line Mix */}
      <Card className="p-6 mb-8" data-id="element-5679">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5680">
          Production Mix by Product Line
        </h3>
        <div className="space-y-4" data-id="element-5681">
          {productLineMixData.map((item, index) => <div key={index} data-id="element-5682">
              <div className="flex items-center justify-between mb-2" data-id="element-5683">
                <span className="text-sm font-medium text-gray-700" data-id="element-5684">
                  {item.line}
                </span>
                <div className="text-right" data-id="element-5685">
                  <span className="text-sm font-semibold text-gray-800" data-id="element-5686">
                    {item.output.toLocaleString()} units
                  </span>
                  <span className="text-xs text-gray-600 ml-2" data-id="element-5687">
                    ({item.percentage}%)
                  </span>
                </div>
              </div>
              <div className="relative h-6 bg-gray-100 rounded-lg overflow-hidden" data-id="element-5688">
                <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600" style={{
              width: `${item.percentage}%`
            }} data-id="element-5689" />
              </div>
            </div>)}
        </div>
      </Card>
      {/* Plant-wise Summary Table */}
      <Card className="p-6" data-id="element-5690">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5691">
          Plant-wise Performance Summary
        </h3>
        <div className="overflow-x-auto" data-id="element-5692">
          <table className="w-full" data-id="element-5693">
            <thead data-id="element-5694">
              <tr className="border-b-2 border-gray-300" data-id="element-5695">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5696">
                  Plant
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5697">
                  Output (Units)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5698">
                  OEE %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5699">
                  Availability %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5700">
                  Performance %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5701">
                  Quality %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5702">
                  Scrap Rate %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5703">
                  Cost/Unit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5704">
                  Efficiency
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5705">
                  Downtime %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5706">
                  Utilization %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5707">
              {plantSummaryData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5708">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5709">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5710">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5711">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.oee >= 80 ? 'bg-green-100 text-green-700' : row.oee >= 75 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5712">
                      {row.oee}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5713">
                    {row.availability}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5714">
                    {row.performance}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5715">
                    {row.quality}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5716">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.scrapRate <= 1.9 ? 'bg-green-100 text-green-700' : row.scrapRate <= 2.2 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5717">
                      {row.scrapRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5718">
                    ₹{row.costPerUnit}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5719">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.efficiency >= 93 ? 'bg-green-100 text-green-700' : row.efficiency >= 91 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5720">
                      {row.efficiency}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5721">
                    {row.downtime}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5722">
                    {row.utilizationRate}%
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-5723">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-5724">AVERAGE</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5725">
                  612,500
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5726">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5727">
                    78.1%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5728">
                  88.1%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5729">
                  91.9%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5730">
                  96.6%
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5731">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5732">
                    2.1%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5733">
                  ₹141
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5734">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5735">
                    92
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5736">
                  12.0%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5737">
                  85.8%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5738">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5739">
            <div className="text-center" data-id="element-5740">
              <div className="text-gray-600" data-id="element-5741">Total Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5742">2.45M Units</div>
            </div>
            <div className="text-center" data-id="element-5743">
              <div className="text-gray-600" data-id="element-5744">Best OEE</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5745">
                Chennai (82%)
              </div>
            </div>
            <div className="text-center" data-id="element-5746">
              <div className="text-gray-600" data-id="element-5747">Best Scrap Rate</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5748">
                Chennai (1.8%)
              </div>
            </div>
            <div className="text-center" data-id="element-5749">
              <div className="text-gray-600" data-id="element-5750">Lowest Cost/Unit</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5751">
                Chennai (₹138)
              </div>
            </div>
            <div className="text-center" data-id="element-5752">
              <div className="text-gray-600" data-id="element-5753">Best Efficiency</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5754">
                Chennai (94)
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ManufacturingOverviewDashboard;